/**
 * Spec 027's provider of 026's picture extension points, mounted around the
 * story editor: the status line and Look row (`topRow`), Generate on slot
 * cards (`slotAction`), the Generate flow or the off note in the side panel
 * (`panelAction`), "Draft all" on the handoff (`allAction`) and Generate on
 * the studio strip (`stripAction`), plus the look gate (the style picker).
 */
import React, { useEffect, useMemo, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { PictureSlot } from '../../../../Shared/src/@types';
import { isSlotNeeded } from '../../../../Shared/src/pictures';
import { registerSessionReset } from '../../api/session';
import { useImageGenStore } from '../../stores/imageGenStore';
import { useStoryStore } from '../../stores/storyStore';
import { PictureExtensionsContext, type PictureExtensions } from '../pictures/pictureExtensions';
import { PictureOwnerContext, usePictureOwner } from '../pictures/pictureOwner';
import DraftAll from './DraftAll';
import GenerateButton, { SlotGenerateButton } from './GenerateButton';
import GenerateFlow from './GenerateFlow';
import { briefTextFor, IMAGE_TEXT } from './imageGenText';
import { LookGatePicker } from './LookPicker';
import LookRow from './LookRow';
import styles from './ImageGen.module.scss';

registerSessionReset(() => useImageGenStore.getState().reset());

/** A slot card: Generate on a needed slot, Generate new on a filled one (D-21), or its job's progress. */
const SlotAction: React.FC<{ slot: PictureSlot }> = ({ slot }) => {
  const entry = useImageGenStore((s) => s.entries[slot.id]);
  if (entry && entry.status === 'interrupted') {
    return <span className={styles.muted} data-testid="card-interrupted">{IMAGE_TEXT.interrupted}</span>;
  }
  // A job whose drafts are running or waiting for a pick.
  if (entry && (entry.status === 'running' || entry.status === 'ready') && !entry.accepted) {
    const ready = entry.drafts.filter((d) => d.status === 'ready').length;
    return (
      <span className={styles.progress} data-testid="card-progress" data-status={entry.status}>
        {entry.status === 'running' && <span className={styles.spinner} />}
        {IMAGE_TEXT.ready(ready, entry.drafts.length)}
      </span>
    );
  }
  return <SlotGenerateButton slot={slot} where="card" />;
};

/** The side panel: the flow, or why there is no Generate. */
const PanelAction: React.FC<{ slot: PictureSlot }> = ({ slot }) => {
  const settings = useImageGenStore((s) => s.settings);
  const story = useStoryStore((s) => s.story);
  const owner = usePictureOwner();
  if (!settings) return null;
  if (settings.mode === 'off' || !settings.available) {
    if (!isSlotNeeded(slot)) return null;
    return (
      <p className={styles.note} data-testid="image-off-note">
        {settings.mode === 'off' ? IMAGE_TEXT.offNote : IMAGE_TEXT.notSetUp}
      </p>
    );
  }
  // A scene owner's flow reads the scene from the owner (spec 030).
  if (owner.ref.kind === 'scene') return <GenerateFlow slot={slot} />;
  return story ? <GenerateFlow slot={slot} story={story} /> : null;
};

/** A studio strip row: Generate for the row's selected expression, then the Pictures view on it. */
const StripAction: React.FC<{ slot?: PictureSlot }> = ({ slot }) => {
  const [, setParams] = useSearchParams();
  const noun = usePictureOwner().noun;
  if (!slot || !isSlotNeeded(slot)) return null;
  return (
    <GenerateButton
      slotIds={[slot.id]}
      blockedReason={(slot.brief ?? '').trim() ? undefined : briefTextFor(noun).needsBrief}
      request={slot.kind === 'character' && slot.framing ? { framings: { [slot.id]: slot.framing } } : undefined}
      testId="strip-generate"
      onStart={(r) => {
        if (r === 'failed') return;
        setParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set('view', 'pictures');
          next.set('slot', slot.id);
          return next;
        }, { replace: false });
      }}
    />
  );
};

/**
 * Mounted around the story editor, or (spec 030) the standalone scene editor
 * inside its `PictureOwnerContext`: the image store follows the owner.
 */
export const ImageGenExtensions: React.FC<{ children: ReactNode }> = ({ children }) => {
  const storyId = useStoryStore((s) => s.story?.id);
  const provided = React.useContext(PictureOwnerContext);
  const sceneId = provided?.ref.kind === 'scene' ? provided.ref.id : undefined;
  useEffect(() => { void useImageGenStore.getState().loadSettings(); }, []);
  useEffect(() => {
    if (sceneId !== undefined) void useImageGenStore.getState().setOwner(sceneId > 0 ? { kind: 'scene', id: sceneId } : undefined);
    else void useImageGenStore.getState().setStory(storyId);
  }, [storyId, sceneId]);

  const value = useMemo<PictureExtensions>(() => ({
    topRow: ({ owner }) => <LookRow owner={owner} />,
    slotAction: (slot) => <SlotAction slot={slot} />,
    panelAction: (slot) => <PanelAction slot={slot} />,
    allAction: (needed) => <DraftAll needed={needed} />,
    stripAction: (_sprite, slot) => <StripAction slot={slot} />,
    slotsReset: (slotIds) => useImageGenStore.getState().forget(slotIds),
  }), []);

  return (
    <PictureExtensionsContext.Provider value={value}>
      {children}
      <LookGatePicker />
    </PictureExtensionsContext.Provider>
  );
};

export default ImageGenExtensions;
